var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useMemo, useRef, useEffect } from 'react';
import { t } from 'i18next';
import { announce } from '../helper/announcer';
import '../styles/widgets/linearPiece.scss';
import { DataContext } from '../player/DataContext';
import { selectTool } from '../helper/helper';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import customEventHandler from '../helper/customEvents';
import { getEdgeSlotCenter } from '../helper/linearPieceSnapHelper';
import globalStore from '../store';
var CELL_SIZE = 110;
// Drop-snap halo (canvas px) for the linear-piece <-> coloredTiles snap.
// Multiplied by tileScale in computeSnappedCenter so it scales with the tile.
// Halo intentionally stays in canvas px (no divide by zoomLevel): when the
// canvas is zoomed in, the halo renders proportionally larger on screen,
// matching the larger visual size of tiles and making the snap easier to hit.
var SNAP_THRESHOLD = 55;
function resolveSnapSlot(cx, cy, orientation, manips) {
    var _a;
    var isH = orientation === 'horizontal';
    for (var _i = 0, manips_1 = manips; _i < manips_1.length; _i++) {
        var item = manips_1[_i];
        if (item.data.type !== 'coloredTiles')
            continue;
        var td = item.data;
        var tileScale = (_a = td.scale) !== null && _a !== void 0 ? _a : 1;
        var scaledCell = CELL_SIZE * tileScale;
        var w = td.columns * scaledCell;
        var h = td.rows * scaledCell;
        var tLeft = td.center.x - w / 2;
        var tRight = td.center.x + w / 2;
        var tTop = td.center.y - h / 2;
        var tBottom = td.center.y + h / 2;
        if (isH) {
            if (Math.abs(cy - (tTop - 21 * tileScale)) < 5 && cx >= tLeft - 5 && cx <= tRight + 5) {
                var col = Math.max(0, Math.min(td.columns - 1, Math.round((cx - tLeft - scaledCell / 2) / scaledCell)));
                return { tileId: td.id, slotIndex: col, orientation: 'horizontal', side: 'top' };
            }
            if (Math.abs(cy - (tBottom + 21 * tileScale)) < 5 && cx >= tLeft - 5 && cx <= tRight + 5) {
                var col = Math.max(0, Math.min(td.columns - 1, Math.round((cx - tLeft - scaledCell / 2) / scaledCell)));
                return { tileId: td.id, slotIndex: col, orientation: 'horizontal', side: 'bottom' };
            }
        }
        else {
            if (Math.abs(cx - (tLeft - 22 * tileScale)) < 5 && cy >= tTop - 5 && cy <= tBottom + 5) {
                var row = Math.max(0, Math.min(td.rows - 1, Math.round((cy - tTop - scaledCell / 2) / scaledCell)));
                return { tileId: td.id, slotIndex: row, orientation: 'vertical', side: 'left' };
            }
            if (Math.abs(cx - (tRight + 22 * tileScale)) < 5 && cy >= tTop - 5 && cy <= tBottom + 5) {
                var row = Math.max(0, Math.min(td.rows - 1, Math.round((cy - tTop - scaledCell / 2) / scaledCell)));
                return { tileId: td.id, slotIndex: row, orientation: 'vertical', side: 'right' };
            }
        }
    }
    return null;
}
var LinearPiece = function (_a) {
    var _b;
    var data = _a.data;
    var _c = useContext(DataContext), manipulative = _c.manipulative, setManipulative = _c.setManipulative, selectedTool = _c.selectedTool, setSelectedTool = _c.setSelectedTool, setSelectedManipulativeId = _c.setSelectedManipulativeId, panningEnable = _c.panningEnable, setContextMenu = _c.setContextMenu;
    var zoomLevel = globalStore.useStoreState(function (s) { return s.playerStore; }).zoomLevel;
    var dataRef = useRef(data);
    useEffect(function () { dataRef.current = data; }, [data]);
    var selectedToolRef = useRef(selectedTool);
    useEffect(function () { selectedToolRef.current = selectedTool; }, [selectedTool]);
    var manipulativeRef = useRef(manipulative);
    useEffect(function () { manipulativeRef.current = manipulative; }, [manipulative]);
    // Wrapper element used for direct-DOM updates during multi-select / group drags
    // (mirrors ColoredTiles pattern — avoids per-frame setManipulative latency).
    var wrapperRef = useRef(null);
    // Latest leader-side position computed during drag, committed to React state on drop.
    var leaderLatestCenterRef = useRef(null);
    // Keyboard / VoiceOver context-menu refs
    var lastFocusWasScreenReader = useRef(false);
    var prevSelectedToolRef = useRef([]);
    var isTouchPrimary = typeof window !== 'undefined' && !!((_b = window.matchMedia) === null || _b === void 0 ? void 0 : _b.call(window, '(pointer: coarse)').matches);
    // Keyboard arrow-key movement state. Movement session begins on the
    // first arrow keydown (when start ref is null) and ends on keyup, which
    // performs the snap commit + saveState + announcement. Mirrors the
    // NumberCharts onKeyUp pattern (no setTimeout debounce — sustained arrow
    // holds naturally produce one save per release).
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var keyMoveCurScaleRef = useRef(null);
    // Auto-focus the wrapper on selection (mirrors Protractor / ColoredTiles pattern)
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(data.id);
        var nowFocused = selectedTool.includes(data.id);
        prevSelectedToolRef.current = selectedTool;
        if (nowFocused && !wasFocused && !data.groupId) {
            setTimeout(function () {
                var _a, _b;
                if ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))
                    return;
                (_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }, 100);
        }
    }, [selectedTool, data.id, data.groupId]);
    // Effectively selected when own id is in selection OR the parent group is selected.
    // Mirrors the coloredTiles pattern so a grouped linearPiece reacts to group-level
    // events (scale, multi-select drag) and shows selection feedback.
    var isSelected = selectedTool.includes(data.id) ||
        (!!data.groupId && selectedTool.includes(data.groupId));
    // ─── Scale listener ───
    useEffect(function () {
        if (!isSelected)
            return;
        var onScale = function (_a) {
            var id = _a.id, panel = _a.panel;
            if (panel !== 'bottomPanel' || (id !== 'scalePlus' && id !== 'scaleMinus'))
                return;
            // Group scaling is handled by GameController — skip here to avoid double-scaling
            if (dataRef.current.groupId)
                return;
            var isPlus = id === 'scalePlus';
            var oldScale;
            var newScale;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a;
                    if (item.data.type !== 'linearPiece' || item.data.id !== data.id)
                        return item;
                    oldScale = (_a = item.data.scale) !== null && _a !== void 0 ? _a : 1;
                    newScale = isPlus
                        ? Math.min(5, Math.round((oldScale + 0.1) * 10) / 10)
                        : Math.max(0.5, Math.round((oldScale - 0.1) * 10) / 10);
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale }) });
                });
            });
            if (oldScale !== undefined && newScale !== undefined) {
                saveState({
                    type: data.id,
                    id: data.id.split('_')[1],
                    propertyName: 'scale',
                    old: JSON.stringify(oldScale),
                    new: JSON.stringify(newScale),
                });
                var isH_1 = dataRef.current.orientation === 'horizontal';
                announce(t('text.manipulativeScaled', {
                    name: t(isH_1 ? 'text.horizontalLinearPiece' : 'text.verticalLinearPiece'),
                    direction: t(isPlus ? 'text.up' : 'text.down'),
                    value: newScale.toFixed(1),
                }));
            }
        };
        customEventHandler.addEventListener('bottomPanel', onScale);
        return function () { return customEventHandler.removeEventListener('bottomPanel', onScale); };
    }, [isSelected, data.id, setManipulative]);
    // ─── Multi-select / group drag listener ───
    // Mirror ColoredTiles: register on group membership alone (don't gate on
    // selectedTool here). Gating on selectedTool meant the listener only
    // registered AFTER React committed setSelectedTool — but the leader's
    // onGridMouseDown dispatches `multiSelectMoveStart` synchronously in the
    // same handler that calls setSelectedTool, so a first-click drag of a
    // grouped ColoredTiles fired the start event before the LinearPiece
    // listener existed. The start event sets `myStart`; missing it left the
    // piece's offset wrong for the entire drag (it didn't follow). Per-event
    // gating (sameGroup || inMultiSel) inside onMove handles the actual
    // filtering, so the outer condition can safely loosen.
    useEffect(function () {
        var isGroupMember = !!data.groupId;
        var isMultiSelected = selectedTool.includes(data.id) && selectedTool.length > 1;
        if (!isGroupMember && !isMultiSelected)
            return;
        var myStart = { x: dataRef.current.center.x, y: dataRef.current.center.y };
        var lastAppliedPos = null;
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === data.id)
                return;
            myStart = { x: dataRef.current.center.x, y: dataRef.current.center.y };
            lastAppliedPos = null;
        };
        var onMove = function (_a) {
            var _b;
            var deltaX = _a.deltaX, deltaY = _a.deltaY, sourceId = _a.sourceId, srcGroupId = _a.groupId;
            if (sourceId === data.id || ((_b = dataRef.current.lock) === null || _b === void 0 ? void 0 : _b.positioning))
                return;
            // Mirror coloredTiles: respond when source shares my groupId OR I'm in the same multi-select.
            var sameGroup = !!srcGroupId && dataRef.current.groupId === srcGroupId;
            // When this piece is a group member, the group's own id — not this piece's
            // individual id — is what's in selectedTool. Checking only `data.id` left group
            // members motionless when the leader is a different type (no groupId payload).
            var inMultiSel = (selectedToolRef.current.includes(data.id) ||
                (!!dataRef.current.groupId && selectedToolRef.current.includes(dataRef.current.groupId))) &&
                selectedToolRef.current.length > 1;
            if (!sameGroup && !inMultiSel)
                return;
            // Direct-DOM update — the final position is committed to React state in onEnd below.
            var el = wrapperRef.current;
            if (!el)
                return;
            var newLeft = myStart.x + deltaX;
            var newTop = myStart.y + deltaY;
            el.style.left = "".concat(newLeft, "px");
            el.style.top = "".concat(newTop, "px");
            lastAppliedPos = { x: newLeft, y: newTop };
        };
        var onEnd = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === data.id)
                return;
            // Do NOT clear inline styles — React will overwrite them once setManipulative below
            // commits new data.center. Clearing first causes a 1-frame flash back to pre-drag.
            if (!lastAppliedPos)
                return;
            // A coloredTiles/linearPiece leader already batch-commits every follower's final
            // center in its own onUp handler (ColoredTiles.tsx's isGroupDragRef block) — self
            // committing here too would double-write and duplicate the undo entry. Only
            // self-commit when the leader is some OTHER type (Dice, a card deck, Shades, ...),
            // since nobody else ever writes the DOM-only position above back into `data.center`
            // — leaving state stale and causing a jump back on the next drag's onStart.
            var sourceItem = manipulativeRef.current.find(function (m) { return m.data.id === sourceId; });
            var sourceIsSelfManaged = (sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.data.type) === 'coloredTiles' || (sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.data.type) === 'linearPiece';
            if (sourceIsSelfManaged) {
                lastAppliedPos = null;
                return;
            }
            var oldCenter = __assign({}, dataRef.current.center);
            var newCenter = __assign({}, lastAppliedPos);
            lastAppliedPos = null;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter }) }) : item;
                });
            });
            var entry = {
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'center',
                old: JSON.stringify(oldCenter),
                new: JSON.stringify(newCenter),
            };
            if (!registerMultiSelectSaveEntries(sourceId, [entry])) {
                saveState(entry);
            }
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
        };
    }, [selectedTool, data.id, data.groupId, setManipulative]);
    var computeSnappedCenter = function (cx, cy) {
        var _a, _b, _c, _d, _e;
        var manips = manipulativeRef.current;
        var isH = dataRef.current.orientation === 'horizontal';
        var candidates = [];
        var _loop_1 = function (item) {
            if (item.data.type !== 'coloredTiles')
                return "continue";
            var td = item.data;
            var tileScale = (_a = td.scale) !== null && _a !== void 0 ? _a : 1;
            var scaledCell = CELL_SIZE * tileScale;
            var w_1 = td.columns * scaledCell;
            var h_1 = td.rows * scaledCell;
            var tLeft = td.center.x - w_1 / 2;
            var tRight = td.center.x + w_1 / 2;
            var tTop = td.center.y - h_1 / 2;
            var tBottom = td.center.y + h_1 / 2;
            var leftVIds = (_b = td.snappedVerticalPieceIds) !== null && _b !== void 0 ? _b : [];
            var rightVIds = (_c = td.snappedRightVerticalPieceIds) !== null && _c !== void 0 ? _c : [];
            var topHIds = (_d = td.snappedHorizontalPieceIds) !== null && _d !== void 0 ? _d : [];
            var bottomHIds = (_e = td.snappedBottomHorizontalPieceIds) !== null && _e !== void 0 ? _e : [];
            // Scale-aware threshold: halo grows with the tile. Stays in canvas px so
            // a higher canvas zoom makes the halo render larger on screen (matching the
            // bigger visible tile), keeping the snap easy to hit at any zoom level.
            var threshold = SNAP_THRESHOLD * tileScale;
            var push = function (sx, sy, slot, side, slotOrientation) {
                candidates.push({
                    x: sx,
                    y: sy,
                    scale: tileScale,
                    tileId: td.id,
                    slotIndex: slot,
                    slotOrientation: slotOrientation,
                    side: side,
                    dist: Math.hypot(sx - cx, sy - cy),
                });
            };
            if (isH) {
                // Top edge
                if (Math.abs(cy - tTop) < threshold && cx >= tLeft - threshold && cx <= tRight + threshold) {
                    var col = Math.max(0, Math.min(td.columns - 1, Math.floor((cx - tLeft) / scaledCell)));
                    var occ = topHIds[col];
                    if (!occ || occ === data.id) {
                        push(tLeft + col * scaledCell + scaledCell / 2, tTop - 21 * tileScale, col, 'top', 'horizontal');
                    }
                }
                // Bottom edge
                if (Math.abs(cy - tBottom) < threshold && cx >= tLeft - threshold && cx <= tRight + threshold) {
                    var col = Math.max(0, Math.min(td.columns - 1, Math.floor((cx - tLeft) / scaledCell)));
                    var occ = bottomHIds[col];
                    if (!occ || occ === data.id) {
                        push(tLeft + col * scaledCell + scaledCell / 2, tBottom + 21 * tileScale, col, 'bottom', 'horizontal');
                    }
                }
            }
            else {
                // Left edge
                if (Math.abs(cx - tLeft) < threshold && cy >= tTop - threshold && cy <= tBottom + threshold) {
                    var row = Math.max(0, Math.min(td.rows - 1, Math.floor((cy - tTop) / scaledCell)));
                    var occ = leftVIds[row];
                    if (!occ || occ === data.id) {
                        push(tLeft - 22 * tileScale, tTop + row * scaledCell + scaledCell / 2, row, 'left', 'vertical');
                    }
                }
                // Right edge
                if (Math.abs(cx - tRight) < threshold && cy >= tTop - threshold && cy <= tBottom + threshold) {
                    var row = Math.max(0, Math.min(td.rows - 1, Math.floor((cy - tTop) / scaledCell)));
                    var occ = rightVIds[row];
                    if (!occ || occ === data.id) {
                        push(tRight + 22 * tileScale, tTop + row * scaledCell + scaledCell / 2, row, 'right', 'vertical');
                    }
                }
            }
        };
        for (var _i = 0, manips_2 = manips; _i < manips_2.length; _i++) {
            var item = manips_2[_i];
            _loop_1(item);
        }
        if (candidates.length === 0)
            return { x: cx, y: cy };
        candidates.sort(function (a, b) { return a.dist - b.dist; });
        var _f = candidates[0], _dist = _f.dist, best = __rest(_f, ["dist"]);
        return best;
    };
    // ─── Shared commit pipeline ───
    // Finalize a move (keyboard arrows OR context-menu Move): write the new
    // center + scale, clear stale snap-array references on every tile that
    // still points at this piece, register the new snap slot (if any), batch
    // a single saveState entry for the whole change, and fire the snap /
    // unsnap announcement. Mirrors the mouse-drop pipeline so the three input
    // modes stay behaviourally aligned.
    //
    // `forceUnlink`: when true, skip new-slot registration even if the final
    // position happens to satisfy resolveSnapSlot. Used by the context-menu
    // path to honour the "move = unlink" rule for previously-snapped pieces —
    // otherwise a perpendicular nudge along the snap edge (e.g. moveDown on a
    // left-snapped piece) keeps re-registering against the same edge because
    // resolveSnapSlot's perpendicular tolerance covers the full edge length.
    var commitMove = function (startCenter, finalCenter, finalScale, forceUnlink) {
        var _a, _b;
        if (forceUnlink === void 0) { forceUnlink = false; }
        if (finalCenter.x === startCenter.x && finalCenter.y === startCenter.y)
            return;
        var orientation = dataRef.current.orientation;
        var oldScale = (_a = dataRef.current.scale) !== null && _a !== void 0 ? _a : 1;
        var committedScale = finalScale !== null && finalScale !== void 0 ? finalScale : oldScale;
        var prevSnap = resolveSnapSlot(startCenter.x, startCenter.y, orientation, manipulativeRef.current);
        var newSnap = forceUnlink
            ? null
            : resolveSnapSlot(finalCenter.x, finalCenter.y, orientation, manipulativeRef.current);
        var sideToProperty = {
            left: 'snappedVerticalPieceIds',
            right: 'snappedRightVerticalPieceIds',
            top: 'snappedHorizontalPieceIds',
            bottom: 'snappedBottomHorizontalPieceIds',
        };
        var allFields = [
            'snappedVerticalPieceIds',
            'snappedRightVerticalPieceIds',
            'snappedHorizontalPieceIds',
            'snappedBottomHorizontalPieceIds',
        ];
        var tileSnapshots = {};
        var captureTile = function (tileId) {
            var _a, _b, _c, _d;
            if (tileSnapshots[tileId])
                return;
            var tile = manipulativeRef.current.find(function (m) { return m.data.id === tileId && m.data.type === 'coloredTiles'; });
            if (!tile)
                return;
            var td = tile.data;
            tileSnapshots[tileId] = {
                snappedVerticalPieceIds: __spreadArray([], ((_a = td.snappedVerticalPieceIds) !== null && _a !== void 0 ? _a : Array(td.rows).fill(null)), true),
                snappedRightVerticalPieceIds: __spreadArray([], ((_b = td.snappedRightVerticalPieceIds) !== null && _b !== void 0 ? _b : Array(td.rows).fill(null)), true),
                snappedHorizontalPieceIds: __spreadArray([], ((_c = td.snappedHorizontalPieceIds) !== null && _c !== void 0 ? _c : Array(td.columns).fill(null)), true),
                snappedBottomHorizontalPieceIds: __spreadArray([], ((_d = td.snappedBottomHorizontalPieceIds) !== null && _d !== void 0 ? _d : Array(td.columns).fill(null)), true),
            };
        };
        if (prevSnap === null || prevSnap === void 0 ? void 0 : prevSnap.tileId)
            captureTile(prevSnap.tileId);
        if (newSnap === null || newSnap === void 0 ? void 0 : newSnap.tileId)
            captureTile(newSnap.tileId);
        // Sweep every tile that still references this piece — catches the case
        // where the piece drifted out of its snap halo (resolveSnapSlot returns
        // null at startCenter) but the source tile's snap array hasn't been
        // cleared yet. Without this sweep the tile would keep the stale id and
        // re-snap the piece on its next re-render (e.g. after a tile resize).
        manipulativeRef.current.forEach(function (item) {
            if (item.data.type !== 'coloredTiles')
                return;
            var td = item.data;
            if (allFields.some(function (f) { var _a; return (_a = td[f]) === null || _a === void 0 ? void 0 : _a.includes(data.id); })) {
                captureTile(td.id);
            }
        });
        var tileNewArrays = {};
        Object.keys(tileSnapshots).forEach(function (tileId) {
            var snap = tileSnapshots[tileId];
            var next = {
                snappedVerticalPieceIds: __spreadArray([], snap.snappedVerticalPieceIds, true),
                snappedRightVerticalPieceIds: __spreadArray([], snap.snappedRightVerticalPieceIds, true),
                snappedHorizontalPieceIds: __spreadArray([], snap.snappedHorizontalPieceIds, true),
                snappedBottomHorizontalPieceIds: __spreadArray([], snap.snappedBottomHorizontalPieceIds, true),
            };
            allFields.forEach(function (f) {
                next[f].forEach(function (v, i) {
                    if (v === data.id)
                        next[f][i] = null;
                });
            });
            if ((newSnap === null || newSnap === void 0 ? void 0 : newSnap.tileId) === tileId) {
                next[sideToProperty[newSnap.side]][newSnap.slotIndex] = data.id;
            }
            tileNewArrays[tileId] = next;
        });
        setManipulative(function (prev) {
            return prev.map(function (item) {
                if (item.data.id === data.id && item.data.type === 'linearPiece') {
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: finalCenter, scale: committedScale }) });
                }
                if (item.data.type === 'coloredTiles') {
                    var td = item.data;
                    var upd = tileNewArrays[td.id];
                    if (!upd)
                        return item;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, td), upd) });
                }
                return item;
            });
        });
        var entries = [];
        entries.push({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'center',
            old: JSON.stringify(startCenter),
            new: JSON.stringify(finalCenter),
        });
        if (committedScale !== oldScale) {
            entries.push({
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'scale',
                old: JSON.stringify(oldScale),
                new: JSON.stringify(committedScale),
            });
        }
        Object.keys(tileSnapshots).forEach(function (tileId) {
            var snap = tileSnapshots[tileId];
            var next = tileNewArrays[tileId];
            allFields.forEach(function (f) {
                var oldJson = JSON.stringify(snap[f]);
                var newJson = JSON.stringify(next[f]);
                if (oldJson === newJson)
                    return;
                entries.push({
                    type: tileId,
                    id: tileId.split('_')[1],
                    propertyName: f,
                    old: oldJson,
                    new: newJson,
                });
            });
        });
        saveState(entries);
        if (newSnap) {
            var edgeKeyMap = {
                left: 'text.linearPieceSnappedLeft',
                right: 'text.linearPieceSnappedRight',
                top: 'text.linearPieceSnappedTop',
                bottom: 'text.linearPieceSnappedBottom',
            };
            var edgeKey = (_b = edgeKeyMap[newSnap.side]) !== null && _b !== void 0 ? _b : 'text.linearPieceSnappedLeft';
            announce(t(edgeKey, { row: newSnap.slotIndex + 1, col: newSnap.slotIndex + 1 }));
        }
        else if (prevSnap) {
            announce(t('text.linearPieceUnsnapped'));
        }
    };
    // ─── Context-menu Move listener ───
    // The generic ContextMenu Move branch only writes center + d. For linearPiece
    // that skips snap detection AND leaves the previously snapped tile pointing
    // at this piece (so the next tile resize re-renders the piece back into the
    // slot). Route those moves through commitMove instead.
    useEffect(function () {
        var onContextMove = function (_a) {
            var _b, _c, _d, _e;
            var manipulativeId = _a.manipulativeId, dx = _a.dx, dy = _a.dy;
            if (manipulativeId !== data.id)
                return;
            if ((_b = dataRef.current.lock) === null || _b === void 0 ? void 0 : _b.positioning)
                return;
            var startCenter = __assign({}, dataRef.current.center);
            // Snapshot snap state at the START — if the piece was already snapped,
            // any context-menu Move is intent to unlink (matches mouse drag and
            // ColoredTiles-move behaviour). Pass that intent into commitMove so the
            // new position doesn't get re-registered against the edge it's still
            // sliding along.
            var wasSnapped = resolveSnapSlot(startCenter.x, startCenter.y, dataRef.current.orientation, manipulativeRef.current) !== null;
            var rawCx = startCenter.x + dx;
            var rawCy = startCenter.y + dy;
            var snapped = computeSnappedCenter(rawCx, rawCy);
            // Snap halo (55 * tileScale) is much wider than MOVE_STEP (10). Two
            // failure modes follow if snap is applied unconditionally:
            //   1. Piece starts at the slot: snap pulls raw back to slot →
            //      finalCenter == startCenter → commitMove no-ops, stuck.
            //   2. Piece sits inside the halo a few px away from the slot
            //      (e.g. slot - 10 after one nudge-away): snap pulls raw back
            //      to slot → next press yanks the piece BACK into the slot,
            //      cancelling the previous press → forth-and-back bounce.
            // Both manifest as snap "fighting" the user's direction. Detect
            // that by comparing the projection vector (snap - raw) to the
            // requested delta: if snap pulls in the opposite direction with
            // magnitude ≥ the move, bypass snap and apply raw delta. Same
            // pattern the patternShapes context-menu Move uses.
            var snapDx = snapped.x - rawCx;
            var snapDy = snapped.y - rawCy;
            var fightsX = dx !== 0 &&
                Math.sign(snapDx) !== 0 &&
                Math.sign(snapDx) !== Math.sign(dx) &&
                Math.abs(snapDx) >= Math.abs(dx);
            var fightsY = dy !== 0 &&
                Math.sign(snapDy) !== 0 &&
                Math.sign(snapDy) !== Math.sign(dy) &&
                Math.abs(snapDy) >= Math.abs(dy);
            var useRaw = fightsX || fightsY;
            // If the piece was already snapped, bypass the snap pull-back too so
            // it visibly drifts (cell-distance per press) rather than getting yanked
            // back to its slot. The forceUnlink flag prevents re-registration
            // against the edge the piece is still travelling along.
            var finalCenter = useRaw || wasSnapped ? { x: rawCx, y: rawCy } : { x: snapped.x, y: snapped.y };
            var finalScale = useRaw || wasSnapped
                ? ((_c = dataRef.current.scale) !== null && _c !== void 0 ? _c : 1)
                : ((_e = (_d = snapped.scale) !== null && _d !== void 0 ? _d : dataRef.current.scale) !== null && _e !== void 0 ? _e : 1);
            commitMove(startCenter, finalCenter, finalScale, wasSnapped);
        };
        customEventHandler.addEventListener('linearPieceContextMove', onContextMove);
        return function () {
            return customEventHandler.removeEventListener('linearPieceContextMove', onContextMove);
        };
    }, [data.id]);
    // ─── Context-menu Snap / Unsnap listeners ───
    // Keyboard-accessible counterpart to mouse drag-snap, driven by the
    // LinearPieceSnapPicker (which the "Snap to…" context-menu item opens).
    useEffect(function () {
        var onSnapToSlot = function (_a) {
            var _b, _c;
            var pieceId = _a.pieceId, tileId = _a.tileId, side = _a.side, slot = _a.slot;
            if (pieceId !== data.id || ((_b = dataRef.current.lock) === null || _b === void 0 ? void 0 : _b.positioning))
                return;
            var tileItem = manipulativeRef.current.find(function (m) { return m.data.id === tileId && m.data.type === 'coloredTiles'; });
            if (!tileItem)
                return;
            var tile = tileItem.data;
            var target = getEdgeSlotCenter(tile, side, slot);
            // forceUnlink=false → commitMove re-detects the slot at `target` (within the
            // ±5px halo) and registers the piece there, repositions, adopts tile scale,
            // saves a batched undo entry, and announces.
            commitMove(__assign({}, dataRef.current.center), target, (_c = tile.scale) !== null && _c !== void 0 ? _c : 1, false);
            // If the target tile belongs to a group, the piece joins it so it moves /
            // scales with the group (consistent with snapped pieces auto-joining at
            // group creation). data.scale is already the tile scale after commitMove.
            if (tile.groupId) {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        var _a;
                        return item.data.id === data.id
                            ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { groupId: tile.groupId, beforeGroupScale: (_a = tile.scale) !== null && _a !== void 0 ? _a : 1, groupScale: 1 }) }) : item;
                    });
                });
            }
        };
        var onUnsnap = function (_a) {
            var _b;
            var pieceId = _a.pieceId;
            if (pieceId !== data.id)
                return;
            var startCenter = __assign({}, dataRef.current.center);
            var scale = (_b = dataRef.current.scale) !== null && _b !== void 0 ? _b : 1;
            var cur = resolveSnapSlot(startCenter.x, startCenter.y, dataRef.current.orientation, manipulativeRef.current);
            // Pop the piece off the edge (one cell perpendicular) so the center changes
            // — commitMove early-returns on an unchanged center — then forceUnlink=true
            // clears its slot on every tile without re-registering, and announces.
            var pop = CELL_SIZE * scale;
            var dx = 0;
            var dy = 0;
            if ((cur === null || cur === void 0 ? void 0 : cur.side) === 'left')
                dx = -pop;
            else if ((cur === null || cur === void 0 ? void 0 : cur.side) === 'right')
                dx = pop;
            else if ((cur === null || cur === void 0 ? void 0 : cur.side) === 'top')
                dy = -pop;
            else if ((cur === null || cur === void 0 ? void 0 : cur.side) === 'bottom')
                dy = pop;
            else
                dx = pop; // registered but resolver missed it — nudge so the sweep runs
            commitMove(startCenter, { x: startCenter.x + dx, y: startCenter.y + dy }, scale, true);
        };
        customEventHandler.addEventListener('snapLinearPieceToSlot', onSnapToSlot);
        customEventHandler.addEventListener('unsnapLinearPiece', onUnsnap);
        return function () {
            customEventHandler.removeEventListener('snapLinearPieceToSlot', onSnapToSlot);
            customEventHandler.removeEventListener('unsnapLinearPiece', onUnsnap);
        };
    }, [data.id]);
    // ─── Drag handler ───
    var onMouseDown = function (e) {
        var _a, _b;
        e.stopPropagation();
        // When this piece is in a group, clicking selects the group (mirrors coloredTiles).
        var selectId = (_a = data.groupId) !== null && _a !== void 0 ? _a : data.id;
        var newSelection = selectTool(e, selectId, selectedToolRef.current, false);
        setSelectedTool(newSelection);
        setSelectedManipulativeId(data.id);
        if ((_b = data.lock) === null || _b === void 0 ? void 0 : _b.positioning)
            return;
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        var startCenter = __assign({}, data.center);
        var initialOldCenter = __assign({}, data.center);
        var hasMoved = false;
        // Group drag: this piece carries a groupId — every group sibling should follow.
        var isGroupDrag = !!data.groupId;
        var groupMembers = isGroupDrag
            ? manipulativeRef.current.filter(function (item) { return item.data.id !== undefined && item.data.groupId === data.groupId; })
            : [];
        // Multi-select drag: ad-hoc selection of multiple items. Unioned with group
        // membership (not exclusive) so dragging a grouped piece that's ALSO part of a
        // larger ad-hoc selection carries the whole selection along, not just its group siblings.
        var isMultiSelected = selectedToolRef.current.length > 1 && selectedToolRef.current.includes(data.id);
        // Checking only item.data.id misses OTHER selected groups entirely: a selected
        // group's own id (not each member's individual id) is what's in selectedTool (see
        // ColoredTilesSelectionGroup.tsx) — this left those members' final position never
        // committed to state at drag end, so they'd track correctly during the drag
        // (DOM-only) then jump back the moment anything re-read data.center.
        var multiSelectedItems = isMultiSelected
            ? manipulativeRef.current.filter(function (item) {
                if (item.data.id === undefined)
                    return false;
                var itemGroupId = item.data.groupId;
                return (selectedToolRef.current.includes(item.data.id) ||
                    (!!itemGroupId && selectedToolRef.current.includes(itemGroupId)));
            })
            : [];
        var followerMap = new Map(__spreadArray(__spreadArray([], groupMembers, true), multiSelectedItems, true).map(function (item) { return [item.data.id, item]; }));
        followerMap.delete(data.id);
        var followers = Array.from(followerMap.values());
        var multiStartPositions = followers.map(function (item) { return ({
            id: item.data.id,
            center: __assign({}, item.data.center),
        }); });
        // Dispatch a coordinated move when there are followers other than the leader.
        var willDispatchGroupMove = multiStartPositions.length > 1;
        if (willDispatchGroupMove) {
            customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: data.id });
            // Reset leader fast-path tracker for this drag.
            leaderLatestCenterRef.current = null;
        }
        var onMove = function (moveEvent) {
            if (moveEvent.cancelable)
                moveEvent.preventDefault();
            var mx = 'clientX' in moveEvent ? moveEvent.clientX : moveEvent.touches[0].clientX;
            var my = 'clientY' in moveEvent ? moveEvent.clientY : moveEvent.touches[0].clientY;
            var dx = (mx - clientX) / zoomLevel;
            var dy = (my - clientY) / zoomLevel;
            if (Math.abs(dx) > 2 || Math.abs(dy) > 2)
                hasMoved = true;
            if (!hasMoved)
                return;
            var newX = startCenter.x + dx;
            var newY = startCenter.y + dy;
            if (willDispatchGroupMove) {
                // NMP/coloredTiles fast path: direct-DOM update, commit on drop. Avoids per-frame
                // React re-renders and keeps leader + followers frame-synced.
                var el = wrapperRef.current;
                if (el) {
                    el.style.left = "".concat(newX, "px");
                    el.style.top = "".concat(newY, "px");
                }
                leaderLatestCenterRef.current = { x: newX, y: newY };
                customEventHandler.dispatchEvent('multiSelectMove', __assign({ deltaX: dx, deltaY: dy, sourceId: data.id }, (isGroupDrag ? { groupId: data.groupId } : {})));
            }
            else {
                // Single drag: keep React-state-driven update so the wrapper position stays
                // bound to data.center (no leftover inline styles).
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        return item.data.id === data.id && item.data.type === 'linearPiece'
                            ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX, y: newY } }) }) : item;
                    });
                });
            }
        };
        var onUp = function (upEvent) {
            var _a, _b, _c;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            if (!hasMoved)
                return;
            var mx = 'clientX' in upEvent ? upEvent.clientX : upEvent.changedTouches[0].clientX;
            var my = 'clientY' in upEvent ? upEvent.clientY : upEvent.changedTouches[0].clientY;
            var dx = (mx - clientX) / zoomLevel;
            var dy = (my - clientY) / zoomLevel;
            var rawCx = startCenter.x + dx;
            var rawCy = startCenter.y + dy;
            // Skip leader snap whenever this drag is coordinating other items (group OR
            // ad-hoc multi-select). Otherwise the leader would re-snap on drop while
            // followers committed at start + raw cursor delta, creating a position
            // jump where the leader stays put / snaps to a cell while the rest of the
            // selection moves with the cursor.
            var snapped = isGroupDrag || willDispatchGroupMove
                ? { x: rawCx, y: rawCy }
                : computeSnappedCenter(rawCx, rawCy);
            var oldScale = (_a = dataRef.current.scale) !== null && _a !== void 0 ? _a : 1;
            var newScale = (_b = snapped.scale) !== null && _b !== void 0 ? _b : oldScale;
            // Resolve previous and new snap slots for registration update. Both carry `side`
            // so we can write to the matching left/right/top/bottom array.
            var prevSnap = resolveSnapSlot(initialOldCenter.x, initialOldCenter.y, dataRef.current.orientation, manipulativeRef.current);
            var newSnapSlot = (snapped.tileId !== undefined && snapped.slotIndex !== undefined && snapped.slotOrientation !== undefined && snapped.side !== undefined)
                ? { tileId: snapped.tileId, slotIndex: snapped.slotIndex, orientation: snapped.slotOrientation, side: snapped.side }
                : null;
            var tileSnapshots = {};
            var captureTileSnapshot = function (tileId) {
                var _a, _b, _c, _d;
                if (tileSnapshots[tileId])
                    return;
                var tile = manipulativeRef.current.find(function (m) { return m.data.id === tileId && m.data.type === 'coloredTiles'; });
                if (!tile)
                    return;
                var td = tile.data;
                tileSnapshots[tileId] = {
                    leftVIds: __spreadArray([], ((_a = td.snappedVerticalPieceIds) !== null && _a !== void 0 ? _a : Array(td.rows).fill(null)), true),
                    rightVIds: __spreadArray([], ((_b = td.snappedRightVerticalPieceIds) !== null && _b !== void 0 ? _b : Array(td.rows).fill(null)), true),
                    topHIds: __spreadArray([], ((_c = td.snappedHorizontalPieceIds) !== null && _c !== void 0 ? _c : Array(td.columns).fill(null)), true),
                    bottomHIds: __spreadArray([], ((_d = td.snappedBottomHorizontalPieceIds) !== null && _d !== void 0 ? _d : Array(td.columns).fill(null)), true),
                };
            };
            if (prevSnap === null || prevSnap === void 0 ? void 0 : prevSnap.tileId)
                captureTileSnapshot(prevSnap.tileId);
            if (newSnapSlot === null || newSnapSlot === void 0 ? void 0 : newSnapSlot.tileId)
                captureTileSnapshot(newSnapSlot.tileId);
            // Reverse-lookup unlink: scan ALL tile snap arrays for stale references to this
            // piece. Handles cases where resolveSnapSlot's tolerance misses a drifted piece
            // (e.g. tile was moved/scaled after snapping).
            var staleFields = ['snappedVerticalPieceIds', 'snappedRightVerticalPieceIds', 'snappedHorizontalPieceIds', 'snappedBottomHorizontalPieceIds'];
            manipulativeRef.current.forEach(function (item) {
                if (item.data.type !== 'coloredTiles')
                    return;
                var td = item.data;
                var hasRef = staleFields.some(function (f) { var _a; return (_a = td[f]) === null || _a === void 0 ? void 0 : _a.includes(data.id); });
                if (!hasRef)
                    return;
                // Only capture tiles not already snapshotted (prevSnap/newSnap tiles already included above).
                if (!tileSnapshots[td.id])
                    captureTileSnapshot(td.id);
            });
            // Helper to pick the right array based on side.
            var arrayFieldForSide = function (side) {
                switch (side) {
                    case 'left': return 'snappedVerticalPieceIds';
                    case 'right': return 'snappedRightVerticalPieceIds';
                    case 'top': return 'snappedHorizontalPieceIds';
                    case 'bottom': return 'snappedBottomHorizontalPieceIds';
                }
            };
            // Commit follower (group / multi-select) final positions in the same setManipulative
            // pass — they were moved via direct DOM during drag and need React state to catch up
            // before the next render, otherwise inline styles snap back to the stale data.center.
            var followerStarts = new Map(multiStartPositions
                .filter(function (sp) { return sp.id !== data.id; })
                .map(function (sp) { return [sp.id, sp.center]; }));
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a;
                    if (item.data.id === data.id && item.data.type === 'linearPiece') {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: snapped.x, y: snapped.y }, scale: newScale }) });
                    }
                    // Commit follower position based on the final drop delta.
                    if (willDispatchGroupMove && item.data.id && followerStarts.has(item.data.id)) {
                        if ((_a = item.data.lock) === null || _a === void 0 ? void 0 : _a.positioning)
                            return item;
                        var start = followerStarts.get(item.data.id);
                        var newCenter = { x: start.x + dx, y: start.y + dy };
                        // Tiles still need their snap-array updates below — handle those before returning.
                        // Group drag: tile and linearPiece moved by the same delta so relative positions
                        // (and snap registration) are unchanged — skip snap-array mutations.
                        if (item.data.type === 'coloredTiles' && !isGroupDrag) {
                            var td_1 = item.data;
                            var newArrays_1 = {};
                            var getCurrent_1 = function (field) {
                                var _a;
                                if (!newArrays_1[field]) {
                                    var lengthField = field.includes('Vertical') ? td_1.rows : td_1.columns;
                                    newArrays_1[field] = __spreadArray([], ((_a = td_1[field]) !== null && _a !== void 0 ? _a : Array(lengthField).fill(null)), true);
                                }
                                return newArrays_1[field];
                            };
                            if (tileSnapshots[td_1.id]) {
                                staleFields.forEach(function (f) {
                                    var arr = getCurrent_1(f);
                                    arr.forEach(function (v, i) { if (v === data.id)
                                        arr[i] = null; });
                                });
                            }
                            if ((prevSnap === null || prevSnap === void 0 ? void 0 : prevSnap.tileId) === td_1.id) {
                                var field = arrayFieldForSide(prevSnap.side);
                                var arr = getCurrent_1(field);
                                if (arr[prevSnap.slotIndex] === data.id)
                                    arr[prevSnap.slotIndex] = null;
                            }
                            if ((newSnapSlot === null || newSnapSlot === void 0 ? void 0 : newSnapSlot.tileId) === td_1.id) {
                                var field = arrayFieldForSide(newSnapSlot.side);
                                var arr = getCurrent_1(field);
                                arr[newSnapSlot.slotIndex] = data.id;
                            }
                            return __assign(__assign({}, item), { data: __assign(__assign(__assign({}, item.data), { center: newCenter }), newArrays_1) });
                        }
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter }) });
                    }
                    // Group drag: snap arrays unchanged — skip mutation on non-follower tiles too.
                    if (item.data.type === 'coloredTiles' && !isGroupDrag) {
                        var td_2 = item.data;
                        var newArrays_2 = {};
                        var getCurrent_2 = function (field) {
                            var _a;
                            if (!newArrays_2[field]) {
                                var lengthField = field.includes('Vertical') ? td_2.rows : td_2.columns;
                                newArrays_2[field] = __spreadArray([], ((_a = td_2[field]) !== null && _a !== void 0 ? _a : Array(lengthField).fill(null)), true);
                            }
                            return newArrays_2[field];
                        };
                        // Reverse-lookup: clear stale references on any tile snapshotted above.
                        if (tileSnapshots[td_2.id]) {
                            staleFields.forEach(function (f) {
                                var arr = getCurrent_2(f);
                                arr.forEach(function (v, i) { if (v === data.id)
                                    arr[i] = null; });
                            });
                        }
                        // Only set the prevSnap slot null if THIS piece actually owned it (handled above but guard for safety).
                        if ((prevSnap === null || prevSnap === void 0 ? void 0 : prevSnap.tileId) === td_2.id) {
                            var field = arrayFieldForSide(prevSnap.side);
                            var arr = getCurrent_2(field);
                            if (arr[prevSnap.slotIndex] === data.id) {
                                arr[prevSnap.slotIndex] = null;
                            }
                        }
                        if ((newSnapSlot === null || newSnapSlot === void 0 ? void 0 : newSnapSlot.tileId) === td_2.id) {
                            var field = arrayFieldForSide(newSnapSlot.side);
                            var arr = getCurrent_2(field);
                            arr[newSnapSlot.slotIndex] = data.id;
                        }
                        if (Object.keys(newArrays_2).length === 0)
                            return item;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), newArrays_2) });
                    }
                    return item;
                });
            });
            // G1: build a single saveState batch — piece center + (optional) scale + each
            // affected tile's snap-array property changes + multi-select followers.
            var entries = [];
            entries.push({
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'center',
                old: JSON.stringify(initialOldCenter),
                new: JSON.stringify({ x: snapped.x, y: snapped.y }),
            });
            if (newScale !== oldScale) {
                entries.push({
                    type: data.id,
                    id: data.id.split('_')[1],
                    propertyName: 'scale',
                    old: JSON.stringify(oldScale),
                    new: JSON.stringify(newScale),
                });
            }
            // Compute the new snap arrays per affected tile per side by re-running the
            // prev/newSnap mutations against the snapshot (mirrors the setManipulative branch).
            var sideToField = {
                left: 'leftVIds',
                right: 'rightVIds',
                top: 'topHIds',
                bottom: 'bottomHIds',
            };
            var sideToProperty = {
                left: 'snappedVerticalPieceIds',
                right: 'snappedRightVerticalPieceIds',
                top: 'snappedHorizontalPieceIds',
                bottom: 'snappedBottomHorizontalPieceIds',
            };
            var sideKeys = Object.keys(sideToField);
            // Group drag: snap arrays are unchanged so there are no tile-array saveState entries.
            if (!isGroupDrag)
                Object.entries(tileSnapshots).forEach(function (_a) {
                    var tileId = _a[0], snapshot = _a[1];
                    var newArrs = {};
                    var getNew = function (side) {
                        var f = sideToField[side];
                        if (!newArrs[f])
                            newArrs[f] = __spreadArray([], snapshot[f], true);
                        return newArrs[f];
                    };
                    // Reverse-lookup: clear stale references in the snapshot arrays.
                    sideKeys.forEach(function (side) {
                        var snapArr = snapshot[sideToField[side]];
                        snapArr.forEach(function (v, i) {
                            if (v === data.id)
                                getNew(side)[i] = null;
                        });
                    });
                    if ((newSnapSlot === null || newSnapSlot === void 0 ? void 0 : newSnapSlot.tileId) === tileId) {
                        getNew(newSnapSlot.side)[newSnapSlot.slotIndex] = data.id;
                    }
                    // Only emit entries where the array actually changed (skip no-ops).
                    Object.keys(newArrs).forEach(function (field) {
                        var oldJson = JSON.stringify(snapshot[field]);
                        var newJson = JSON.stringify(newArrs[field]);
                        if (oldJson === newJson)
                            return;
                        var side = sideKeys.find(function (s) { return sideToField[s] === field; });
                        entries.push({
                            type: tileId,
                            id: tileId.split('_')[1],
                            propertyName: sideToProperty[side],
                            old: oldJson,
                            new: newJson,
                        });
                    });
                });
            if (multiStartPositions.length > 1) {
                multiStartPositions.forEach(function (sp) {
                    var _a;
                    entries.push({
                        type: sp.id,
                        id: (_a = sp.id.split('_')[1]) !== null && _a !== void 0 ? _a : '',
                        propertyName: 'center',
                        old: JSON.stringify(sp.center),
                        new: JSON.stringify({ x: sp.center.x + dx, y: sp.center.y + dy }),
                    });
                });
                customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: data.id });
            }
            // Skip the entire saveState if only the piece center changed to the same value
            // (dropped in same spot with no snap change).
            var centerEntry = entries[0];
            var isNoOp = entries.length === 1 && centerEntry.propertyName === 'center' &&
                centerEntry.old === centerEntry.new;
            if (!isNoOp) {
                // `entries` already includes the multi-select followers' own entries (pushed above when
                // multiStartPositions.length > 1) alongside this piece's own — route the WHOLE array
                // through the shared coordinator so it merges with any OTHER contributor's entries for
                // this same gesture into one undo step, instead of this piece calling saveState on its
                // own regardless of what else moved.
                if (multiStartPositions.length <= 1 ||
                    !registerMultiSelectSaveEntries(data.id, entries)) {
                    saveState(entries);
                }
            }
            // Snap/unsnap announcements
            if (newSnapSlot) {
                var edgeKeyMap = {
                    left: 'text.linearPieceSnappedLeft',
                    right: 'text.linearPieceSnappedRight',
                    top: 'text.linearPieceSnappedTop',
                    bottom: 'text.linearPieceSnappedBottom',
                };
                var edgeKey = (_c = edgeKeyMap[newSnapSlot.side]) !== null && _c !== void 0 ? _c : 'text.linearPieceSnappedLeft';
                var scaleChanged = newScale !== oldScale;
                if (scaleChanged) {
                    announce(t('text.linearPieceSnappedWithScale', { edge: t(edgeKey, { row: newSnapSlot.slotIndex + 1, col: newSnapSlot.slotIndex + 1 }), scale: newScale.toFixed(1) }));
                }
                else {
                    announce(t(edgeKey, { row: newSnapSlot.slotIndex + 1, col: newSnapSlot.slotIndex + 1 }));
                }
            }
            else if (prevSnap) {
                announce(t('text.linearPieceUnsnapped'));
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
    };
    var isH = data.orientation === 'horizontal';
    var w = data.dimensions.width * (data.scale || 1);
    var h = data.dimensions.height * (data.scale || 1);
    // Find this piece's slot index inside the tile it's snapped to (if any).
    // Each ColoredTile holds four arrays mapping slot index → linear-piece id
    // for the four edges. We walk them all and return the slot index for the
    // edge this piece sits on. Used to give the piece a distinguishable VO
    // label like "Vertical linear piece 2" when multiple pieces are snapped
    // to the same tile. Unsnapped pieces fall through to the plain label.
    var snapIndex = useMemo(function () {
        for (var _i = 0, manipulative_1 = manipulative; _i < manipulative_1.length; _i++) {
            var item = manipulative_1[_i];
            if (item.data.type !== 'coloredTiles')
                continue;
            var td = item.data;
            var arrays = [
                td.snappedVerticalPieceIds,
                td.snappedRightVerticalPieceIds,
                td.snappedHorizontalPieceIds,
                td.snappedBottomHorizontalPieceIds,
            ];
            for (var _a = 0, arrays_1 = arrays; _a < arrays_1.length; _a++) {
                var arr = arrays_1[_a];
                if (!arr)
                    continue;
                var idx = arr.findIndex(function (id) { return id === data.id; });
                if (idx !== -1)
                    return idx;
            }
        }
        return null;
    }, [manipulative, data.id]);
    var baseAriaLabel = t(isH ? 'text.horizontalLinearPiece' : 'text.verticalLinearPiece');
    var effectiveAriaLabel = snapIndex !== null ? "".concat(baseAriaLabel, " ").concat(snapIndex + 1) : baseAriaLabel;
    return (_jsx("div", __assign({ ref: wrapperRef, className: "linearPieceWidget".concat(isSelected ? ' selected' : ''), style: {
            left: data.center.x,
            top: data.center.y,
            width: w,
            height: h,
        }, onMouseDown: onMouseDown, onTouchStart: onMouseDown, "data-manipulative-id": data.groupId ? undefined : data.id }, (data.groupId ? { 'data-group-id': data.groupId } : {}), { "aria-label": effectiveAriaLabel, "aria-hidden": data.groupId ? true : undefined, role: "button", tabIndex: data.groupId ? -1 : 0, onFocus: function (e) {
            var _a, _b;
            try {
                lastFocusWasScreenReader.current = !!((_b = (_a = e.target).matches) === null || _b === void 0 ? void 0 : _b.call(_a, ':focus-visible'));
            }
            catch (_c) {
                lastFocusWasScreenReader.current = false;
            }
        }, onBlur: function () {
            lastFocusWasScreenReader.current = false;
        }, onKeyDown: function (e) {
            var _a, _b, _c, _d, _e;
            if (data.groupId || panningEnable)
                return;
            // Arrow-key movement (mirror of mouse drag). Each press nudges the
            // center by `step` canvas px (5 by default, 20 with Shift, divided
            // by zoomLevel so screen-distance feels consistent at any zoom).
            // computeSnappedCenter is applied per-press so the piece visually
            // snaps to tile edges as it moves, exactly like dragging it. A
            // debounce timer commits the final snap-array bookkeeping +
            // saveState + snap/unsnap announcement once the user stops pressing
            // (matches the Protractor / ColoredTiles keyboard-move pattern).
            if (e.key === 'ArrowUp' ||
                e.key === 'ArrowDown' ||
                e.key === 'ArrowLeft' ||
                e.key === 'ArrowRight') {
                e.preventDefault();
                e.stopPropagation();
                if ((_a = dataRef.current.lock) === null || _a === void 0 ? void 0 : _a.positioning)
                    return;
                if (!selectedTool.includes(data.id))
                    setSelectedTool([data.id]);
                var step = (e.shiftKey ? 20 : 5) / (zoomLevel || 1);
                var delta = {
                    ArrowUp: { x: 0, y: -step },
                    ArrowDown: { x: 0, y: step },
                    ArrowLeft: { x: -step, y: 0 },
                    ArrowRight: { x: step, y: 0 },
                };
                var d = delta[e.key];
                var curPos = (_b = keyMoveCurPosRef.current) !== null && _b !== void 0 ? _b : dataRef.current.center;
                if (!keyMoveStartRef.current) {
                    keyMoveStartRef.current = __assign({}, dataRef.current.center);
                }
                // Live snap while moving — direct-DOM update only. No setManipulative
                // per press: React state catches up once on keyUp (mirrors the
                // mouse-drag NMP-style direct-DOM fast path elsewhere in this file).
                // The snap result drives both the visible position and the scale (so
                // the piece adopts the target tile's scale when it crosses the snap
                // threshold).
                var rawCx = curPos.x + d.x;
                var rawCy = curPos.y + d.y;
                var snapped = computeSnappedCenter(rawCx, rawCy);
                var newCenter = { x: snapped.x, y: snapped.y };
                var newScale = (_d = (_c = snapped.scale) !== null && _c !== void 0 ? _c : dataRef.current.scale) !== null && _d !== void 0 ? _d : 1;
                keyMoveCurPosRef.current = newCenter;
                keyMoveCurScaleRef.current = newScale;
                var el = wrapperRef.current;
                if (el) {
                    el.style.left = "".concat(newCenter.x, "px");
                    el.style.top = "".concat(newCenter.y, "px");
                    var dims = dataRef.current.dimensions;
                    el.style.width = "".concat(dims.width * newScale, "px");
                    el.style.height = "".concat(dims.height * newScale, "px");
                }
                return;
            }
            // Enter / Space / ContextMenu / Shift+F10 all open the context menu.
            // The wrapper is a <div role="button"> — plain divs don't fire a
            // synthetic click on Enter the way native <button> elements do, so
            // each activation key needs explicit handling here.
            if (e.key === 'Enter' ||
                e.key === ' ' ||
                e.key === 'ContextMenu' ||
                (e.shiftKey && e.key === 'F10')) {
                e.preventDefault();
                e.stopPropagation();
                if (!selectedTool.includes(data.id))
                    setSelectedTool([data.id]);
                var rect = (_e = wrapperRef.current) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect();
                if (rect)
                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: data.id });
            }
        }, onKeyUp: function (e) {
            // Arrow-key release: hand off to the shared commit pipeline.
            if (e.key !== 'ArrowUp' &&
                e.key !== 'ArrowDown' &&
                e.key !== 'ArrowLeft' &&
                e.key !== 'ArrowRight')
                return;
            var startCenter = keyMoveStartRef.current;
            var finalCenter = keyMoveCurPosRef.current;
            var finalScale = keyMoveCurScaleRef.current;
            keyMoveStartRef.current = null;
            keyMoveCurPosRef.current = null;
            keyMoveCurScaleRef.current = null;
            if (!startCenter || !finalCenter)
                return;
            commitMove(startCenter, finalCenter, finalScale);
        }, onClick: function (e) {
            var _a;
            if (data.groupId || panningEnable)
                return;
            var isKeyboard = (isTouchPrimary && lastFocusWasScreenReader.current) ||
                (!isTouchPrimary && e.detail === 0);
            if (isKeyboard) {
                if (!selectedTool.includes(data.id))
                    setSelectedTool([data.id]);
                var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                if (rect)
                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: data.id });
            }
        } })));
};
export default LinearPiece;
